import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  NgZone,
  OnDestroy,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { finalize, Subject, takeUntil } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SharedModule } from "../../../shared/shared.module";
import {
  Contact,
  ContactGroup,
  ContactReference,
  ContactSource,
  ExtensionContact,
  ManualContact,
} from "../../interfaces/contact.interface";
import {
  IAddressBookDialogData,
  IAddressBookDialogResult,
  IContactGroupDeleteResult,
  IContactGroupEditDialogData,
  IContactWidget,
} from "../../interfaces/contact-widget.interface";
import { ContactUtils } from "../../utils/contact-utils";
import { ContactWidgetService } from "../../services/contact-widget.service";
import { ContactGroupDeleteComponent } from "../contact-group-delete/contact-group-delete.component";
import { AddressBookDialogComponent } from "./address-book/address-book.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "lib-contact-group-edit",
  imports: [SharedModule],
  templateUrl: "./contact-group-edit.component.html",
  styleUrl: "./contact-group-edit.component.scss",
})
export class ContactGroupEditComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly contactWidgetService: ContactWidgetService =
    inject(ContactWidgetService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly ngZone: NgZone = inject(NgZone);
  protected readonly translate: TranslateService = inject(TranslateService);

  // Form data
  groupName: string = "";
  groupNameError: string = "";
  selectedContactReferences: ContactReference[] = [];
  contactWidget: IContactWidget | undefined;

  // Contact data - reactive signals
  allContacts: WritableSignal<Contact[]> = signal([]);
  isLoadingExtensionContacts: WritableSignal<boolean> = signal(false);

  // Sorting and filtering
  sortBy: WritableSignal<"selection" | "alphabetical"> = signal("selection");
  searchTerm: WritableSignal<string> = signal("");

  // Track changes to manual contacts for parent component
  private manualContactsChanged: boolean = false;
  private readonly createdManualContacts: ManualContact[] = [];
  private updatedManualContacts: ManualContact[] = [];
  private readonly deletedManualContactIds: string[] = [];

  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  // Expose ContactUtils and ContactSource for template
  protected readonly ContactUtils = ContactUtils;
  protected readonly ContactSource = ContactSource;

  // Computed signal for filtered and sorted contacts
  protected displayedContacts: Signal<Contact[]> = computed(() => {
    let contacts = this.allContacts();

    // Apply search filter
    const searchTerm = this.searchTerm().toLowerCase();
    if (searchTerm) {
      contacts = contacts.filter(
        (contact) =>
          contact.name?.toLowerCase().includes(searchTerm) ||
          ContactUtils.getDisplayPhoneNumber(contact)
            .toLowerCase()
            .includes(searchTerm),
      );
    }

    // Apply sorting
    const sortBy = this.sortBy();
    if (sortBy === "selection") {
      // Selected contacts first, then alphabetical within each group
      const selected = contacts
        .filter((contact) => this.isContactSelected(contact))
        .sort((a, b) => a.name.localeCompare(b.name));
      const unselected = contacts
        .filter((contact) => !this.isContactSelected(contact))
        .sort((a, b) => a.name.localeCompare(b.name));
      return [...selected, ...unselected];
    } else {
      // Pure alphabetical sorting
      return contacts.sort((a, b) => a.name.localeCompare(b.name));
    }
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IContactGroupEditDialogData,
    public dialogRef: MatDialogRef<ContactGroupEditComponent>,
  ) {
    this.selectedContactReferences = [...(data.selectedContactIds ?? [])];
    this.groupName = data.groupName ?? "";
    this.contactWidget = data.widget;
  }

  ngAfterViewInit(): void {
    // Initialize with contacts passed from parent (existing manual contacts + any other contacts)
    this.allContacts.set([...this.data.contacts]);

    // Setup socket connection monitoring
    this.setupSocketConnectionMonitoring();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setupSocketConnectionMonitoring(): void {
    if (!this.contactWidget?._id) {
      console.error("[ContactGroupEdit] Widget ID is required");
      return;
    }

    this.contactWidgetService
      .getConnectionState()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((state: "disconnected" | "connecting" | "connected") => {
        this.ngZone.run(() => {
          console.log(`[ContactGroupEdit] Socket connection state: ${state}`);
          if (state === "connected") {
            this.loadAllExtensionContacts();
          }
        });
      });
  }

  private loadAllExtensionContacts(): void {
    if (!this.contactWidget?._id) {
      return;
    }

    this.isLoadingExtensionContacts.set(true);

    this.contactWidgetService
      .requestAllExtensionContacts(this.contactWidget._id)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        finalize(() => this.isLoadingExtensionContacts.set(false)),
      )
      .subscribe({
        next: (extensionContacts: ExtensionContact[]) => {
          console.log(
            `[ContactGroupEdit] Received ${extensionContacts.length} extension contacts`,
          );

          // Merge with existing contacts (avoid duplicates)
          const currentContacts = this.allContacts();
          const extensionContactsToAdd = extensionContacts.filter(
            (extContact: ExtensionContact) =>
              !currentContacts.some(
                (existing: Contact) =>
                  existing.contactId === extContact.contactId &&
                  existing.source === extContact.source,
              ),
          );

          this.allContacts.set([...currentContacts, ...extensionContactsToAdd]);
        },
        error: (error) => {
          console.error(
            "[ContactGroupEdit] Error loading extension contacts:",
            error,
          );
          this.snackBar.open(
            this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.FAILED_TO_LOAD_EXTENSION_CONTACTS",
            ),
            "Close",
            { duration: 3000 },
          );
        },
      });
  }

  // Sorting methods
  setSortBy(sortType: "selection" | "alphabetical"): void {
    const previousSort = this.sortBy();
    this.sortBy.set(sortType);

    // Provide user feedback about the sorting change
    if (
      previousSort !== sortType &&
      this.selectedContactReferences.length > 0
    ) {
      const message =
        sortType === "alphabetical"
          ? this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.CONTACTS_SORTED_ALPHABETICALLY",
            )
          : this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.CONTACTS_SORTED_BY_SELECTION_ORDER",
            );

      this.snackBar.open(message, "", { duration: 1500 });
    }
  }

  // Get sorted selected contacts based on current sort mode
  getSortedSelectedContacts(): ContactReference[] {
    const contacts = [...this.selectedContactReferences];

    if (this.sortBy() === "alphabetical") {
      // Sort alphabetically by contact name
      return contacts.sort((a, b) => {
        const contactA = this.getContactFromReference(a);
        const contactB = this.getContactFromReference(b);

        if (!contactA || !contactB) return 0;

        return contactA.name.localeCompare(contactB.name);
      });
    } else {
      // Sort by selection order (order property)
      return ContactUtils.sortContactReferences(contacts);
    }
  }

  // Get the display position for a contact based on current sort mode
  getContactDisplayPosition(
    contactRef: ContactReference,
    index: number,
  ): number {
    if (this.sortBy() === "alphabetical") {
      // In alphabetical mode, show alphabetical position
      return index + 1;
    } else {
      // In selection mode, show the actual selection order
      return (contactRef.order ?? index) + 1;
    }
  }

  // Contact selection methods
  isContactSelected(contact: Contact): boolean {
    return this.selectedContactReferences.some(
      (ref) =>
        ref.contactId === contact.contactId && ref.source === contact.source,
    );
  }

  // Address Book Methods
  openAddressBook(source: ContactSource): void {
    const data: IAddressBookDialogData = {
      source: source,
      widget: this.contactWidget!,
      alreadySelectedContactIds: this.selectedContactReferences,
      existingContacts: this.allContacts(),
    };

    const dialogRef = this.dialog.open(AddressBookDialogComponent, {
      panelClass: ["astimax-address-book-dialog", "adx-dialog-large"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IAddressBookDialogResult | undefined) => {
        if (result && typeof result === "object") {
          // Handle additions
          if (result.selectedContacts.length > 0) {
            this.addContactsFromAddressBook(result.selectedContacts);
          }

          // Handle removals
          if (result.contactsToRemove && result.contactsToRemove.length > 0) {
            this.removeContactsFromGroup(result.contactsToRemove);
          }

          // Handle manual contact changes from address book
          if (result.manualContactChanges) {
            this.handleManualContactChanges(result.manualContactChanges);
          }
        }
      });
  }

  private addContactsFromAddressBook(contacts: Contact[]): void {
    // Add contacts to local contact list if they don't exist
    const currentContacts = this.allContacts();
    const contactsToAdd = contacts.filter(
      (newContact: Contact) =>
        !currentContacts.some(
          (existing: Contact) =>
            existing.contactId === newContact.contactId &&
            existing.source === newContact.source,
        ),
    );

    if (contactsToAdd.length > 0) {
      this.allContacts.set([...currentContacts, ...contactsToAdd]);
    }

    // Add contact references to selection with proper order
    const newReferences: ContactReference[] = contacts.map(
      (contact: Contact) => ({
        contactId: contact.contactId,
        source: contact.source!,
        order: this.selectedContactReferences.length, // Add at the end
      }),
    );

    // Filter out already selected contacts
    const referencesToAdd = newReferences.filter(
      (newRef: ContactReference) =>
        !this.selectedContactReferences.some(
          (existing: ContactReference) =>
            existing.contactId === newRef.contactId &&
            existing.source === newRef.source,
        ),
    );

    // Add new references with proper order
    let currentOrder = this.selectedContactReferences.length;
    const referencesWithOrder = referencesToAdd.map((ref) => ({
      ...ref,
      order: currentOrder++,
    }));

    this.selectedContactReferences = [
      ...this.selectedContactReferences,
      ...referencesWithOrder,
    ];

    this.snackBar.open(
      `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.ADDED")} ${referencesToAdd.length} ${referencesToAdd.length === 1 ? this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACT") : this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACTS")} ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.TO_GROUP")}`,
      "Close",
      { duration: 3000 },
    );
  }

  // Add this new method to handle contact removals
  private removeContactsFromGroup(contactsToRemove: ContactReference[]): void {
    // const originalCount = this.selectedContactReferences.length;

    // Remove contacts from selection
    this.selectedContactReferences = this.selectedContactReferences.filter(
      (ref: ContactReference) =>
        !contactsToRemove.some(
          (removeRef: ContactReference) =>
            removeRef.contactId === ref.contactId &&
            removeRef.source === ref.source,
        ),
    );

    // Normalize order numbers after removal
    this.selectedContactReferences = ContactUtils.normalizeOrderNumbers(
      this.selectedContactReferences,
    );

    /*const removedCount = originalCount - this.selectedContactReferences.length;
    if (removedCount > 0) {
	    const contactText = removedCount === 1
		    ? this.translate.instant('ASTIMAX.CONTACT.WIDGET.CONTACT')
		    : this.translate.instant('ASTIMAX.CONTACT.WIDGET.CONTACTS');

	    const message = `${this.translate.instant('ASTIMAX.CONTACT.WIDGET.REMOVED')} ${removedCount} ${contactText} ${this.translate.instant('ASTIMAX.CONTACT.WIDGET.FROM_GROUP')}`;

	    this.snackBar.open(message, 'Close', { duration: 3000 });
    }*/
  }

  private handleManualContactChanges(changes: {
    created: ManualContact[];
    updated: ManualContact[];
    deleted: string[];
  }): void {
    // Handle CREATED contacts - Track for later save when user saves group
    if (changes.created.length > 0) {
      this.createdManualContacts.push(...changes.created);
      this.manualContactsChanged = true;

      // Add newly created contacts to allContacts signal
      const currentContacts = this.allContacts();
      const newContacts = changes.created.filter(
        (newContact: ManualContact) =>
          !currentContacts.some(
            (existing: Contact) =>
              existing.contactId === newContact.contactId &&
              existing.source === newContact.source,
          ),
      );

      if (newContacts.length > 0) {
        this.allContacts.set([...currentContacts, ...newContacts]);
      }
    }

    // Handle UPDATED and DELETED contacts - Save immediately
    if (changes.updated.length > 0 || changes.deleted.length > 0) {
      // Track the changes
      if (changes.updated.length > 0) {
        changes.updated.forEach((updatedContact: ManualContact) => {
          const existingUpdateIndex = this.updatedManualContacts.findIndex(
            (c: ManualContact) => c.contactId === updatedContact.contactId,
          );
          if (existingUpdateIndex > -1) {
            this.updatedManualContacts[existingUpdateIndex] = updatedContact;
          } else {
            this.updatedManualContacts.push(updatedContact);
          }
        });
        this.manualContactsChanged = true;
      }

      if (changes.deleted.length > 0) {
        this.deletedManualContactIds.push(...changes.deleted);
        this.manualContactsChanged = true;

        // Remove deleted contacts from selection if they were selected
        const originalSelectionLength = this.selectedContactReferences.length;
        this.selectedContactReferences = this.selectedContactReferences.filter(
          (ref: ContactReference) =>
            !(
              ref.source === ContactSource.MANUAL &&
              changes.deleted.includes(ref.contactId)
            ),
        );

        if (originalSelectionLength !== this.selectedContactReferences.length) {
          console.log(
            `[ContactGroupEdit] Removed ${originalSelectionLength - this.selectedContactReferences.length} deleted manual contact(s) from group selection`,
          );
          // Normalize order numbers after removal
          this.selectedContactReferences = ContactUtils.normalizeOrderNumbers(
            this.selectedContactReferences,
          );
        }
      }

      // Save immediately for edit/delete operations
      this.saveEditDeleteChangesImmediately(changes).then();
    }
  }

  private async saveEditDeleteChangesImmediately(changes: {
    created: ManualContact[];
    updated: ManualContact[];
    deleted: string[];
  }): Promise<void> {
    if (!this.contactWidget?._id) return;

    try {
      console.log(
        `[ContactGroupEdit] Saving edit/delete changes immediately...`,
      );

      // Get current manual contacts from service
      const widgetData = this.contactWidgetService.getWidgetData(
        this.contactWidget._id,
      );
      if (!widgetData) {
        console.error(
          "[ContactGroupEdit] Widget data not found for immediate save",
        );
        return;
      }

      // Apply changes using the same pattern as create component
      const updatedManualContacts = this.applyEditDeleteChanges(
        widgetData.manualContacts,
        changes,
      );

      // Update through service
      this.contactWidgetService.updateManualContacts(
        this.contactWidget._id,
        updatedManualContacts,
        true,
      );

      // Sync allContacts with updated service data
      await this.syncAllContacts(this.contactWidget._id);

      // Save to database immediately for edit/delete changes
      await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
        this.contactWidget._id,
        this.contactWidget,
      );

      console.log(
        `[ContactGroupEdit] Successfully saved ${changes.updated.length} edits and ${changes.deleted.length} deletes to database`,
      );
    } catch (error) {
      console.error(
        "[ContactGroupEdit] Error saving edit/delete changes:",
        error,
      );
      this.snackBar.open(
        this.translate.instant(
          "ASTIMAX.CONTACT.WIDGET.FAILED_TO_SAVE_CONTACT_CHANGES",
        ),
        "Close",
        { duration: 3000 },
      );
    }
  }

  /**
   * Apply only edit and delete changes (not create - those are handled when user saves group)
   */
  private applyEditDeleteChanges(
    currentContacts: ManualContact[],
    changes: { updated: ManualContact[]; deleted: string[] },
  ): ManualContact[] {
    // Create a Map for O(1) lookups
    const contactsMap = new Map<string, ManualContact>();

    // Add existing contacts to map
    currentContacts.forEach((contact) => {
      contactsMap.set(contact.contactId, { ...contact });
    });

    // Apply updates (overwrite existing)
    let updatedCount = 0;
    changes.updated.forEach((updatedContact) => {
      if (contactsMap.has(updatedContact.contactId)) {
        contactsMap.set(updatedContact.contactId, { ...updatedContact });
        updatedCount++;
      }
    });

    // Apply deletions
    let deletedCount = 0;
    changes.deleted.forEach((contactId) => {
      if (contactsMap.delete(contactId)) {
        deletedCount++;
      }
    });

    // Log individual change counts
    if (updatedCount > 0)
      console.log(`[ContactGroupEdit] Updated ${updatedCount} contacts`);
    if (deletedCount > 0)
      console.log(`[ContactGroupEdit] Deleted ${deletedCount} contacts`);

    // Convert map back to array
    return Array.from(contactsMap.values());
  }

  /**
   * Save manual contact changes when user saves the group (includes created contacts)
   */
  private async saveManualContactChanges(): Promise<void> {
    if (!this.contactWidget?._id) {
      throw new Error("[ContactGroupEdit] Widget ID missing");
    }

    // Early exit if no changes to process
    if (!this.hasManualContactChanges()) {
      console.log("[ContactGroupEdit] No manual contact changes to save");
      return;
    }

    const widgetId = this.contactWidget._id;

    try {
      // Get current manual contacts from service (authoritative source)
      const widgetData = this.contactWidgetService.getWidgetData(widgetId);
      if (!widgetData) {
        throw new Error("[ContactGroupEdit] Widget data not found");
      }

      // Apply all changes
      const updatedManualContacts = this.applyAllManualContactChanges(
        widgetData.manualContacts,
      );

      const totalChanges =
        this.createdManualContacts.length +
        this.updatedManualContacts.length +
        this.deletedManualContactIds.length;

      console.log(
        `[ContactGroupEdit] Applying ${totalChanges} manual contact changes: ${widgetData.manualContacts.length} → ${updatedManualContacts.length} contacts`,
      );

      // Update service with processed contacts
      this.contactWidgetService.updateManualContacts(
        widgetId,
        updatedManualContacts,
        true,
      );

      // Sync local allContacts signal with updated service data
      await this.syncAllContacts(widgetId);

      console.log(
        `[ContactGroupEdit] Successfully processed manual contact changes`,
      );
    } catch (error) {
      console.error(
        "[ContactGroupEdit] Error saving manual contact changes:",
        error,
      );
      throw error;
    } finally {
      // Always clear tracking arrays, even if there was an error
      this.clearChangeTracking();
    }
  }

  /**
   * Apply all manual contact changes
   */
  private applyAllManualContactChanges(
    currentContacts: ManualContact[],
  ): ManualContact[] {
    const contactsMap = new Map<string, ManualContact>();

    // Add existing contacts to map
    currentContacts.forEach((contact) => {
      contactsMap.set(contact.contactId, { ...contact });
    });

    // Apply created contacts (only if they don't already exist)
    let createdCount = 0;
    this.createdManualContacts.forEach((createdContact) => {
      if (!contactsMap.has(createdContact.contactId)) {
        contactsMap.set(createdContact.contactId, { ...createdContact });
        createdCount++;
      }
    });

    // Apply updates (overwrite existing)
    let updatedCount = 0;
    this.updatedManualContacts.forEach((updatedContact) => {
      if (contactsMap.has(updatedContact.contactId)) {
        contactsMap.set(updatedContact.contactId, { ...updatedContact });
        updatedCount++;
      }
    });

    // Apply deletions
    let deletedCount = 0;
    this.deletedManualContactIds.forEach((contactId) => {
      if (contactsMap.delete(contactId)) {
        deletedCount++;
      }
    });

    // Log individual change counts
    if (createdCount > 0)
      console.log(`[ContactGroupEdit] Added ${createdCount} created contacts`);
    if (updatedCount > 0)
      console.log(`[ContactGroupEdit] Updated ${updatedCount} contacts`);
    if (deletedCount > 0)
      console.log(`[ContactGroupEdit] Deleted ${deletedCount} contacts`);

    // Convert map back to array
    return Array.from(contactsMap.values());
  }

  /**
   * Check if there are any manual contact changes to process
   */
  private hasManualContactChanges(): boolean {
    return (
      this.createdManualContacts.length > 0 ||
      this.updatedManualContacts.length > 0 ||
      this.deletedManualContactIds.length > 0
    );
  }

  /**
   * Sync allContacts signal with updated service data
   */
  private async syncAllContacts(widgetId: string): Promise<void> {
    const updatedWidgetData = this.contactWidgetService.getWidgetData(widgetId);
    if (!updatedWidgetData) {
      console.warn(
        "[ContactGroupEdit] Could not sync allContacts - widget data not found",
      );
      return;
    }

    // Preserve extension contacts from current allContacts signal
    const currentExtensionContacts = this.allContacts().filter(
      (c: Contact): c is ExtensionContact => ContactUtils.isExtensionContact(c),
    );

    // Create new synced contacts array
    const syncedContacts: Contact[] = [
      ...updatedWidgetData.manualContacts,
      ...currentExtensionContacts,
    ];

    // Update the signal
    this.allContacts.set(syncedContacts);

    console.log(
      `[ContactGroupEdit] Synced allContacts() - ${updatedWidgetData.manualContacts.length} manual + ${currentExtensionContacts.length} extension = ${syncedContacts.length} total contacts`,
    );
  }

  /**
   * Clear all change tracking arrays and flags
   */
  private clearChangeTracking(): void {
    this.createdManualContacts.length = 0;
    this.updatedManualContacts.length = 0;
    this.deletedManualContactIds.length = 0;
    this.manualContactsChanged = false;
  }

  // Contact Data Helpers
  getContactFromReference(contactRef: ContactReference): Contact | undefined {
    return this.allContacts().find(
      (contact: Contact) =>
        contact.contactId === contactRef.contactId &&
        contact.source === contactRef.source,
    );
  }

  removeSelectedContact(contactRef: ContactReference): void {
    this.selectedContactReferences = ContactUtils.removeContact(
      this.selectedContactReferences,
      contactRef,
    );
  }

  getSelectedContactsCount(): number {
    return this.selectedContactReferences.length;
  }

  clearAllSelections(): void {
    this.selectedContactReferences = [];
  }

  selectAllDisplayedContacts(): void {
    const displayedContacts = this.displayedContacts();
    const currentSelectionIds = this.selectedContactReferences.map(
      (ref) => `${ref.contactId}-${ref.source}`,
    );

    // Find new contacts to add
    const newContacts = displayedContacts.filter(
      (contact) =>
        !currentSelectionIds.includes(`${contact.contactId}-${contact.source}`),
    );

    // Add new contacts with proper order numbers
    let currentOrder = this.selectedContactReferences.length;
    const newContactRefs: ContactReference[] = newContacts.map(
      (contact: Contact) => ({
        contactId: contact.contactId,
        source: contact.source!,
        order: currentOrder++,
      }),
    );

    this.selectedContactReferences = [
      ...this.selectedContactReferences,
      ...newContactRefs,
    ];
  }

  // Contact Reordering
  onContactReorder(event: CdkDragDrop<ContactReference[]>): void {
    // When dragging, we need to update the actual selectedContactReferences array
    // Get the current sorted list
    const sortedContacts = this.getSortedSelectedContacts();

    // Use utility to move contact and update all order numbers
    const reorderedContacts = ContactUtils.moveContactToPosition(
      sortedContacts,
      sortedContacts[event.previousIndex],
      event.currentIndex,
    );

    // Update the main selectedContactReferences array
    this.selectedContactReferences = reorderedContacts;

    // If we were in alphabetical mode, switch back to selection order since user manually reordered
    if (this.sortBy() === "alphabetical") {
      this.sortBy.set("selection");
    }
  }

  // Helper methods for safe contact property access
  getDisplayPhoneNumberSafe(contact: Contact | undefined): string {
    if (!contact) return "";

    // Handle extension contacts with number property
    if (
      contact.source === ContactSource.EXTENSION &&
      "number" in contact &&
      contact.number
    ) {
      return contact.number;
    }

    // Fallback to phoneNumber for all contact types
    return contact.phoneNumber || "";
  }

  getSelectedContactAriaLabel(
    contactRef: ContactReference,
    index: number,
  ): string {
    const contact = this.getContactFromReference(contactRef);
    if (!contact) return "Unknown contact";

    const position = this.getContactDisplayPosition(contactRef, index);
    const sortMode =
      this.sortBy() === "alphabetical" ? "alphabetical" : "selection";

    return `${contact.name}, position ${position} of ${this.selectedContactReferences.length} (${sortMode} order), ${this.getDisplayPhoneNumberSafe(contact)}, ${ContactUtils.getContactSourceLabel(contactRef.source, this.translate)}`;
  }

  // Statistics Methods for Address Book Cards
  getManualContactsCount(): number {
    return this.allContacts().filter((c: Contact) =>
      ContactUtils.isManualContact(c),
    ).length;
  }

  getExtensionContactsCount(): number {
    return this.allContacts().filter((c: Contact) =>
      ContactUtils.isExtensionContact(c),
    ).length;
  }

  getSelectedManualContactsCount(): number {
    return this.selectedContactReferences.filter(
      (ref: ContactReference) => ref.source === ContactSource.MANUAL,
    ).length;
  }

  getSelectedExtensionContactsCount(): number {
    return this.selectedContactReferences.filter(
      (ref: ContactReference) => ref.source === ContactSource.EXTENSION,
    ).length;
  }

  onGroupNameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.groupName = target.value;
    this.performValidation(target.value);
  }

  private performValidation(groupName: string): void {
    this.groupNameError = "";

    if (!groupName.trim()) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_REQUIRED",
      );
      return;
    }

    if (groupName.trim().length < 2) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_TOO_SHORT",
      );
      return;
    }

    if (groupName.trim().length > 50) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_TOO_LONG",
      );
      return;
    }

    const existingNames = this.data.existingGroups
      .filter((g: ContactGroup) => g.groupId !== this.data.groupId)
      .map((g: ContactGroup) => g.name.toLowerCase());

    if (existingNames.includes(groupName.trim().toLowerCase())) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_EXISTS",
      );
    }
  }

  // Form validation and actions (valid without selected contacts)
  isFormValid(): boolean {
    return this.groupName.trim().length >= 2 && !this.groupNameError;
  }

  async save(): Promise<void> {
    if (!this.isFormValid()) {
      console.error("[ContactGroupEdit] Cannot save - form is invalid");
      return;
    }

    if (!this.contactWidget?._id) {
      console.error("[ContactGroupEdit] Cannot save - widget ID missing");
      return;
    }

    try {
      // Handle manual contact changes first (includes created contacts)
      if (this.manualContactsChanged) {
        await this.saveManualContactChanges();
      }

      // Get contacts in the current sort order before normalizing
      let contactRefsToSave: ContactReference[];

      if (this.sortBy() === "alphabetical") {
        // If alphabetical sorting is active, use the alphabetically sorted contacts
        // and update their order properties to match the alphabetical sequence
        const sortedContacts = this.getSortedSelectedContacts();
        contactRefsToSave = sortedContacts.map(
          (ref: ContactReference, index: number) => ({
            ...ref,
            order: index,
          }),
        );
      } else {
        // If selection sorting is active, just normalize the existing order
        contactRefsToSave = ContactUtils.normalizeOrderNumbers(
          this.selectedContactReferences,
        );
      }

      // Update the contact group through service
      await this.updateContactGroup(
        this.data.groupId,
        this.groupName.trim(),
        contactRefsToSave,
      );

      // Save the complete widget settings to database
      await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
        this.contactWidget._id,
        this.contactWidget,
      );

      this.snackBar.open(
        `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.GROUP")} "${this.groupName.trim()}" ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.UPDATED_SUCCESSFULLY_WITH")} ${contactRefsToSave.length} ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACTS")}`,
        "Close",
        { duration: 3000 },
      );

      // Close dialog with success result
      this.dialogRef.close({
        widget: this.contactWidget,
        action: "update",
        groupName: this.groupName.trim(),
        selectedContactIds: contactRefsToSave,
        groupId: this.data.groupId,
        success: true,
      });
    } catch (error) {
      console.error("[ContactGroupEdit] Error saving group:", error);
      this.snackBar.open(
        `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.FAILED_TO_SAVE_GROUP_CHANGES")}`,
        "Close",
        { duration: 3000 },
      );
    }
  }

  private async updateContactGroup(
    groupId: number,
    groupName: string,
    contactReferences: ContactReference[],
  ): Promise<void> {
    if (!this.contactWidget?._id) return;

    // Get current groups from service
    const widgetData = this.contactWidgetService.getWidgetData(
      this.contactWidget._id,
    );
    if (!widgetData) {
      throw new Error("Widget data not found");
    }

    const currentGroups = widgetData.contactGroups;
    const updatedGroups = currentGroups.map((group: ContactGroup) =>
      group.groupId === groupId
        ? {
            ...group,
            name: groupName,
            contacts: contactReferences,
            updated: new Date(),
          }
        : group,
    );

    // Update contact groups through service
    this.contactWidgetService.updateContactGroups(
      this.contactWidget._id,
      updatedGroups,
    );

    // Update the widget settings
    if (this.contactWidget.settings) {
      this.contactWidget.settings.contactGroups = updatedGroups;
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  delete(data: IContactGroupEditDialogData): void {
    const deleteDialogRef = this.dialog.open(ContactGroupDeleteComponent, {
      panelClass: ["astimax-contact-group-delete-dialog", "adx-dialog-small"],
      disableClose: true,
      data: {
        widget: this.contactWidget,
        groupId: data.groupId,
        name: data.groupName,
      },
    });

    deleteDialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: IContactGroupDeleteResult | string) => {
        if (result && typeof result === "object" && result.success) {
          // Group was successfully deleted, close this dialog too
          this.dialogRef.close({
            action: "delete",
            groupId: this.data.groupId,
            success: true,
          });
        }
      });
  }
}
